import { useEffect } from 'react';
import { useEmbedStore } from '../embedStore';

// Origines autorisées à héberger l'iframe (doit rester aligné avec frame-ancestors dans netlify.toml)
const ALLOWED_PARENT_ORIGINS = [
  'https://arsenalsports.ca',
  'https://www.arsenalsports.ca',
  'https://toutsurlehockey.com',
  'https://www.toutsurlehockey.com',
];

/**
 * Measure the content wrapper with room for the Arsenal parent's 3px mobile border.
 * Document scrollHeight is at least the viewport height and cannot shrink it.
 */
function getContentHeight(): number {
  const content = document.querySelector<HTMLElement>('.embed-content-wrapper');
  if (content) {
    const parentBorderAllowance = 4;
    return Math.ceil(Math.max(content.scrollHeight, content.getBoundingClientRect().height)) + parentBorderAllowance;
  }
  return Math.max(
    document.documentElement.scrollHeight,
    document.body?.scrollHeight ?? 0,
    document.documentElement.offsetHeight,
    document.body?.offsetHeight ?? 0
  );
}

/**
 * En mode embed (iframe Arsenal/BPM) : signale la hauteur réelle du contenu au parent via
 * postMessage, pour que celui-ci redimensionne l'iframe (un seul scrollbar côté Arsenal,
 * pas de scroll imbriqué). Sans effet si le parent n'écoute pas ce message — non bloquant.
 */
export function useEmbedAutoResize() {
  const isEmbed = useEmbedStore((state) => state.isEmbed);

  useEffect(() => {
    if (!isEmbed || typeof window === 'undefined' || window.parent === window) return;

    // Sans cette classe, html/body gardent `height: 100%` et `scrollHeight` renvoie la hauteur
    // du cadre au lieu de celle du contenu — la hauteur se fige alors définitivement.
    document.documentElement.classList.add('embed-mode');

    let lastHeight = 0;
    let rafId = 0;
    // `forceNextSend` bypasse la déduplication `height === lastHeight` : au retour
    // de background, le parent Arsenal a pu perdre le suivi de la hauteur pendant
    // que RAF était suspendu, donc on re-signale même si la valeur n'a pas bougé.
    let forceNextSend = false;
    let resumeTimeoutId = 0;
    const sendHeight = () => {
      const height = getContentHeight();
      // Never send invalid values
      if (!Number.isFinite(height) || height <= 0) return;
      if (height === lastHeight && !forceNextSend) return;
      forceNextSend = false;
      lastHeight = height;
      const payload = { source: 'dynastypool' as const, type: 'resize' as const, height };
      ALLOWED_PARENT_ORIGINS.forEach((origin) => {
        try {
          window.parent.postMessage(payload, origin);
        } catch {
          // ignore — origine non joignable
        }
      });
    };

    const postHeight = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sendHeight);
    };

    // Observe body size changes (works because embed mode uses height: auto)
    const resizeObserver = new ResizeObserver(() => postHeight());
    resizeObserver.observe(document.body);

    // Observe DOM mutations (SPA navigation, async data, added/removed elements)
    const mutationObserver = new MutationObserver(() => postHeight());
    mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Window resize
    window.addEventListener('resize', postHeight);
    window.addEventListener('load', postHeight);

    // When page returns from background (bfcache, tab switch, iframe hidden then shown),
    // RAF callbacks were suspended — force an immediate height recalculation bypassing RAF.
    // On some mobile browsers (iOS Safari + certains WebViews Android en iframe), la
    // première frame RAF au retour peut être perdue. On envoie donc :
    //   1. immédiatement (sync, hors RAF) avec forceNextSend pour re-signaler la hauteur
    //      même si elle n'a pas bougé numériquement ;
    //   2. une seconde fois après ~200ms via setTimeout (pas RAF) pour attraper la
    //      stabilisation de layout (viewport mobile, safe-area, barre d'URL rétractile).
    const forceResendHeight = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      forceNextSend = true;
      sendHeight();
      if (resumeTimeoutId) {
        clearTimeout(resumeTimeoutId);
      }
      resumeTimeoutId = window.setTimeout(() => {
        resumeTimeoutId = 0;
        forceNextSend = true;
        sendHeight();
      }, 200);
    };

    // Au démarrage (y compris après un window.location.reload() interne), le parent peut ne pas
    // avoir encore attaché son listener : on re-signale plusieurs fois sans déduplication.
    const startupTimeoutIds = [0, 150, 400, 800, 1500, 3000].map((delay) =>
      window.setTimeout(() => {
        forceNextSend = true;
        sendHeight();
      }, delay)
    );

    const handleVisibilityReturn = () => {
      if (document.visibilityState === 'visible') {
        forceResendHeight();
      }
    };
    const handlePageShow = (e: PageTransitionEvent) => {
      // persisted = true means restored from bfcache
      if (e.persisted) {
        forceResendHeight();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityReturn);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleVisibilityReturn);

    // Fonts loaded
    document.fonts?.ready?.then(postHeight).catch(() => {});

    // Safety net for late images, CSS transitions, etc.
    // Toutes les 5 itérations, on ignore la déduplication : si le parent a manqué (ou rejeté) notre
    // dernier message, la hauteur ne changeant plus, plus rien ne serait jamais renvoyé et le cadre
    // resterait trop court indéfiniment (scroll interne bloqué sur mobile).
    let tick = 0;
    const interval = window.setInterval(() => {
      tick += 1;
      if (tick % 5 === 0) {
        forceNextSend = true;
        sendHeight();
        return;
      }
      postHeight();
    }, 1000);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
      startupTimeoutIds.forEach(clearTimeout);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', postHeight);
      window.removeEventListener('load', postHeight);
      document.removeEventListener('visibilitychange', handleVisibilityReturn);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleVisibilityReturn);
      window.clearInterval(interval);
    };
  }, [isEmbed]);
}
